import React, {useState, useEffect, useCallback, useRef} from "react";
import {BottomScrollListener, useBottomScrollListener} from "react-bottom-scroll-listener";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import ErrorModal from "./ErrorModal";
import FullScreenLoadingOverlay from "./FullScreenLoadingOverlay";
import ShipmentDataModal from "./ShipmentDataModal";
import {
  setLocaleInDataPiker,
  parseOrderState,
  parseBigInteger,
  titleize,
  IPAD_WIDTH,
  enterDownHandler, CURRENCY_EURO, parseAmount, pluralize, LOW_DEBOUNCE_TIME, parseUserFullName, DEFAULT_PER_PAGE_PARAM
} from "../../utils/utils";
import RejectingModal from "../../backoffice/components/RejectingModal";
import I18n from 'i18n.js.erb'
import AlertPopup from "./AlertPopup";
import Accordion from 'react-bootstrap/Accordion'
import Card from 'react-bootstrap/Card'
import OverlayTrigger from 'react-bootstrap/OverlayTrigger'
import Tooltip from 'react-bootstrap/Tooltip'
import SuccessFlashMessage from "./SuccessFlashMessage";
import { debounce } from "lodash";

const IndexOrders = ({
     initialOrders,
     ordersType,
     sellers,
     buyers,
     shipmentAddresses,
     sorting_options,
     sorting_directions,
     couriers,
     possibleRejectingCauses,
     possibleOrdersRejectingCauses
   }) => {

  const [initCompleted, setInitCompleted] = useState(false)
  const [orders, setOrders] = useState([]);
  const [ordersCount, setOrdersCount] = useState(0);
  const initialFilters = {
    id: null, state: null, from_date: null, to_date: null, seller_id: null,
    buyer_id: null, shipment_address_id: null, sort_by: 'id', sorting_direction: 'asc'
  }
  const [fromDate, setFromDate] = useState(null)
  const [toDate, setToDate] = useState(null)
  const [filters, setFilters] = useState(initialFilters)
  const [page, setPage] = useState(1);
  const [hasLoadedAllOrders, setHasLoadedAllOrders] = useState(false);
  const [loading, setLoading] = useState(false);
  const [ showSuccessFlashMessage, setShowSuccessFlashMessage ] = useState(false)
  const [showErrorModal, setShowErrorModal] = useState(false)
  const [isOrderInProgress, setIsOrderInProgress] = useState(false);
  const [orderToSetShipmentData, setOrderToSetShipmentData] = useState(null)
  const [shipmentDataToUpdate, setShipmentDataToUpdate] = useState(null)
  const [orderToReject, setOrderToReject] = useState(null);
  const [rejectingCause, setRejectingCause] = useState("not_received");
  const [otherRejectingCause, setOtherRejectingCause] = useState("");
  const [filterPannelIsOpen, setFilterPannelIsOpen] = useState(false);
  const [showChangeStateAlertPopup, setShowChangeStateAlertPopup] = useState(false);
  const [activeOrder, setActiveOrder] = useState(null);
  const [activeOrderKey, setactiveOrderKey] = useState(null);
  const [activeOrderState, setActiveOrderState] = useState(null);
  const [ couriersToRender, setCouriersToRender ] = useState(couriers);
  const [activeOrderCourier, setActiveOrderCourier] = useState(null);
  const [activeOrderTrackingNumber, setActiveOrderTrakingNumber] = useState(null);
  

  useEffect(() => {
    if (initCompleted && orders.length === 0) {
      getOrders()
    }
  }, [orders]);


  useEffect(() => {
    if (!initCompleted) {
      const oiriginalOrders = JSON.parse(initialOrders);
      setInitCompleted(true);
      appendOrders(oiriginalOrders.orders);
      setOrdersCount(oiriginalOrders.orders_count);
      enterDownHandler(setFilterPannelIsOpen);
    }
  }, []);

  useEffect(() => {
    if (initCompleted) {
      debouncedUpdateFilters();
      return debouncedUpdateFilters.cancel;
    }
  }, [filters.id, debouncedUpdateFilters]);


  // Hook che permette il caricamento di nuovi ordini da renderizzare sull'interfaccia
  // grafica non appena l'utente scrolla fino in fondo alla pagina (INFINITE SCROLL)
  const scrollRef = useBottomScrollListener(() => getOrders());


  // Funzione che permette di ottenere dal server nuovi ordini da renderizzare sull'interfaccia grafica
  const getOrders = () => {
    if (!hasLoadedAllOrders) {
      let url = `/orders.json?page=${page}&per_page=${DEFAULT_PER_PAGE_PARAM}&orders_type=${ordersType}`
      Object.entries(filters).forEach(([fieldName, fieldValue]) => {
        if (fieldValue !== null)
          url += `&${fieldName}=${fieldValue}`
      })

      fetch(url, {
        method: 'GET'
      })
        .then(response => response.json())
        .then((data) => {
          setLoading(false)
          appendOrders(data.orders)
          setOrdersCount(data.orders_count)
        })
        .catch((error) => {
          console.error("ERROR:", error);
          setShowErrorModal(true);
        });
    }
  }


  // Funzione che permette di concatenare nuovi ordini ottenuti del server all'array che contiene tutti gli
  // ordini da mostrare sull'interfaccia grafica all'utente
  const appendOrders = (newOrders) => {
    setOrders(orders.concat(newOrders));
    setPage(page + 1)
    if (newOrders.length < DEFAULT_PER_PAGE_PARAM)
      setHasLoadedAllOrders(true)
  }


  // Funzione che permette di ottenere dal server gli ordini dell'utente che matchano con i filtri
  // di ricerca da lui specificati sull'interfaccia grafica
  const getOrdersByMatchingFilters = (filter, value) => {
    const newFilters = {...filters}
    newFilters[filter] = (filter === 'from_date' || filter === 'to_date') && value ? value.getFullYear() + '-' + (value.getMonth() + 1) + '-' + value.getDate() : value;
    updateFilters(newFilters)
  }


  // Funzione che aggiorna i filtri di ricerca degli ordini specificati dall'utente sull'interfaccia grafica e rilancia
  // la ricerca sugli ordini che matchano con i nuovi filtri specificati
  const updateFilters = (newFilters) => {
    setFilters(newFilters)
    setLoading(true)
    setPage(1)
    setHasLoadedAllOrders(false)
    setOrders([]);
  }


  //Funzione che esegue l'update debouncato del filtro di ricerca degli ordini per id
  const debouncedUpdateFilters = useCallback(
    debounce(() => updateFilters(filters), LOW_DEBOUNCE_TIME),
    [filters.id]
  );


  //Funzione che setta il nuovo filtro di ricerca degli ordini per id
  function onChangeDebouncedFilterHandler(key, value) {
    const newFilters = {...filters}
    newFilters[key] = value;
    setFilters(newFilters)
  }


  // Funzione che permette di modificare lo stato corrente di un ordine
  const onChangeOrderStateHandler = (e, order, key, initialValue, newValue, courierId, newCourier, trackingNumber, problem) => {
    let can_update = true;

    if (key === 'state') {
      can_update = false;
      if ((ordersType === 'received' && initialValue === 'opened' && ['in_progress', 'sent'].includes(newValue)) ||
        (ordersType === 'received' && initialValue === 'opened' && newValue === 'rejected' && problem !== null && problem !== "") ||
        (ordersType === 'received' && initialValue === 'in_progress' && newValue === 'sent' && (courierId || newCourier)) ||
        (ordersType === 'sent' && ['sent', 'warning'].includes(initialValue) && newValue === 'received') ||
        (ordersType === 'sent' && ['sent', 'warning'].includes(initialValue) && newValue === 'warning' && problem !== null && problem !== "" && order.can_warn_a_problem))
        can_update = true
    }

    if (can_update) {
      const body = {}
      if (courierId || newCourier) {
        body['courier_id'] = courierId
        body['new_courier'] = newCourier
        body['tracking_number'] = trackingNumber
      }

      if (newValue === 'rejected' && problem !== null && problem !== "")
        body['rejection_reason'] = problem

      if (newValue === 'warning' && problem !== null && problem !== "")
        body['rejecting_cause'] = problem

      if (key)
        body[key] = newValue

      const url = `/orders/${order.id}`;

      setIsOrderInProgress(true)

      fetch(url + ".json", {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      })
        .then((res) => {
          setIsOrderInProgress(false)
          if (!res.ok)
            throw Error("An error occour during saving data on the database");
          else
            return res.json()
        })
        .then((data) => {
          const newOrders = orders.map((o) => {
            if (o.id === order.id) {
              o[key] = newValue;
              if (courierId) {
                const new_courier = (couriersToRender.filter((courier) => courier[0] == courierId))[0];
                o['courier'] = {
                  id: new_courier[0],
                  meta_name: new_courier[1]
                }
                o['tracking_number'] = trackingNumber;
              }

              else if (newCourier) {
                setCouriersToRender([ ...couriersToRender ].concat([[data.courier_id, newCourier]]))
                o['courier'] = {
                  id: data.courier_id,
                  meta_name: newCourier
                }
                o['tracking_number'] = trackingNumber;
              }

              if (newValue === 'rejected' && problem !== null && problem !== "") {
                o['rejection_reason'] = problem;
              }
            }
            return o;
          });
          setOrders(newOrders);
          setShowSuccessFlashMessage(true)
        })
        .catch((error) => {
          console.error("ERROR:", error);
          setShowErrorModal(true);
        });
    }

    setOrderToSetShipmentData(null)
    setShipmentDataToUpdate(null)
    if (e)
      e.stopPropagation()
  };


  // Funzione che permette di aprire la modale su cui il seller dovrà immettere i dati di spedizione dell'ordine
  // (corriere e tracking number) una volta che l'avrà spedito al buyer, o di aggiornare tali dati
  const handleShowShipmentDataModal = (e, order) => {
    setOrderToSetShipmentData(order);
    setShipmentDataToUpdate(order.state === 'in_progress' ? null : { courier: { id: order.courier.id, meta_name: order.courier.meta_name }, tracking_number: order.tracking_number })
    e.stopPropagation()
  }


  // Funzione che si occupa di segnalare un problema riscontrato dal buyer relativamente a un certo ordine
  const handleReject = () => {
    if (orderToReject.state === 'opened')
      onChangeOrderStateHandler(null, orderToReject, 'state', orderToReject.state, 'rejected', null, null, null, rejectingCause ? rejectingCause : otherRejectingCause)
    else
      onChangeOrderStateHandler(null, orderToReject, 'state', orderToReject.state, 'warning', null, null, null, rejectingCause ? rejectingCause : otherRejectingCause)
    setOrderToReject(null)
  }

  const handleRejectingCause = (value) => {
    setRejectingCause(value)
    setOtherRejectingCause("");
  }

  const handleSetOtherRejectingCause = (value) => {
    setOtherRejectingCause(value);
    setRejectingCause(null)
  }

  const handleAlertProblem = (e, order) => {
    setOrderToReject(order)
    e.stopPropagation()
  }

  //Funzione che setta gli state usati come parametri per cambiare lo stato di un ordine
  const askToUserTheChengeState = (e, order, key, state, courier, trackingNumber) => {
    e.stopPropagation();
    setActiveOrder(order);
    setactiveOrderKey(key);
    setActiveOrderState(state);
    setActiveOrderCourier(courier);
    setActiveOrderTrakingNumber(trackingNumber);
    setShowChangeStateAlertPopup(true)
  }

  //Funzione che setta il testo che viene mostrato al'utente nel popup di conferma
  // del cambio di stato dell'ordine
  const setaskToUserText = (currentState, newState) => {
    if (currentState === 'warning') {
      return I18n.t('js.indexOrdersPage.modalChangeOrderStatusWarningContent')
    } else if (currentState === 'sent') {
      return I18n.t('js.indexOrdersPage.modalChangeOrderStatusSentContent')
    } else if (newState === 'rejected') {
      return I18n.t('js.indexOrdersPage.modalChangeOrderStatusRejectedContent')
    } else {
      return I18n.t('js.indexOrdersPage.modalChangeOrderStatusContent')
    }
  }

  // Funzione che traduce nella lingua corrente del sito l'eventuale motivo di reject di un ordine
  const translateRejectionReason = (rejectionReason) => {
    if (rejectionReason === 'products_no_longer_available') {
      return I18n.t('js.indexOrdersPage.productsNoLongerAvailable')
    } else if (rejectionReason === 'payment_not_received') {
      return I18n.t('js.indexOrdersPage.paymentNotReceived')
    } else {
      return titleize(rejectionReason)
    }
  }

  return (
    <React.Fragment>
      <div className="w-90 m-auto mt-10px">
        <div className={`${filterPannelIsOpen ? 'mt-submission-with-open-filter' : 'mt-2'}  title-core-pages`}>
          {ordersType === "received" && <h2>{I18n.t('js.indexOrdersPage.received_orders')}</h2>}
          {ordersType === "sent" && <h2>{I18n.t('js.indexOrdersPage.submitted_orders')}</h2>}
          {ordersType !== "sent" && ordersType !== "received" && <h2>{I18n.t('js.indexOrdersPage.all_orders')}</h2>}
        </div>

        {/*filtri*/}

        <div>
          {
            filterPannelIsOpen ?
              <div className="pl-4 pr-4 pb-4 filters-top-bar bg-light overflow-auto expansion--mobile--bt shadow"
                   id="filters">

                <div className="row">
                  <div className="col-12 text-right p-lg-0">
                    <button type="button" className="btn p-0 mt-8px mr-4px"
                            onClick={(e) => setFilterPannelIsOpen(false)}>
                      <i className="fas fa-times show-on-mobile"/>
                      <i className="fas fa-times show-on-desktop-imp"/>
                    </button>
                  </div>
                </div>

                <div className="row mb-2">
                  <div className="col-12">
                    <h4 className="mt-0">{I18n.t('js.filters.by')}</h4>
                  </div>
                </div>

                <div className="row">
                  <div className="col-lg-1 col-md-3 col-6 mb-2">
                    <p className="mb-0">{I18n.t('js.filters.id')}</p>
                    <input
                      type="number"
                      value={filters.id ? filters.id : ""}
                      className={`form-control ${filters.id ? 'border-aiG-blue-imp' : ''}`}
                      placeholder={I18n.t('js.filters.placeholderOrderId')}
                      onChange={(e) => onChangeDebouncedFilterHandler('id', e.target.value)}
                      name="id_filter"
                      id="id_filter"
                    />
                  </div>

                  <div className="col-lg-1 col-md-3 col-6 mb-2">
                    <p className="mb-0">{I18n.t('js.filters.FromDate')}</p>
                    <div className="d-flex">
                      <DatePicker
                        locale={setLocaleInDataPiker()}
                        className={`form-control ${fromDate ? 'border-aiG-blue-imp' : ''}`}
                        selected={fromDate}
                        name="from_date_filter"
                        id="from_date_filter"
                        onChange={date => {
                          setFromDate(date);
                          getOrdersByMatchingFilters("from_date", date);
                        }}
                        withPortal
                      />
                      {
                        fromDate &&
                          <button
                            type="button"
                            className="btn list-icon p-0 ml-2"
                            onClick={(e) => {
                              setFromDate(null);
                              getOrdersByMatchingFilters("from_date", null);
                            }}>
                            <i className="fas fa-backspace"/>
                          </button>
                      }
                    </div>
                  </div>

                  <div className="col-lg-1 col-md-3 col-6 mb-2">
                    <p className="mb-0">{I18n.t('js.filters.ToDate')}</p>
                    <div className="d-flex">
                      <DatePicker
                        locale={setLocaleInDataPiker()}
                        className={`form-control ${toDate ? 'border-aiG-blue-imp' : ''}`}
                        selected={toDate}
                        name="to_date_filter"
                        id="to_date_filter"
                        onChange={date => {
                          setToDate(date);
                          getOrdersByMatchingFilters('to_date', date)
                        }}
                        withPortal
                      />
                      {
                        toDate &&
                          <button
                            type="button"
                            className="btn list-icon p-0 ml-2"
                            onClick={(e) => {
                              setToDate(null);
                              getOrdersByMatchingFilters("to_date", null);
                            }}>
                            <i className="fas fa-backspace"/>
                          </button>
                      }
                    </div>
                  </div>

                  <div className="col-lg-2 col-md-3 col-6 mb-2">
                    <p className="mb-0">{I18n.t('js.filters.state')}</p>
                    <select
                      className={`form-control ${filters.state ? 'border-aiG-blue-imp' : ''} pl-2`}
                      value={filters.state ? filters.state : ""}
                      name="state_filter"
                      id="state_filter"
                      onChange={(e) => getOrdersByMatchingFilters('state', e.target.value)}
                    >
                      <option value={""}>{I18n.t('js.common.selectStatus')}</option>
                      {['opened', 'in_progress', 'sent', 'received', 'warning', 'rejected'].map((state) => (
                        <option value={state} key={state}>
                          {parseOrderState(state)}
                        </option>
                      ))}
                    </select>
                  </div>

                  {
                    ordersType !== 'received' &&
                    <div className="col-lg-2 col-md-3 col-6 mb-2">
                      <p className="mb-0">{I18n.t('js.indexOrdersPage.seller')}</p>
                      <select
                        className={`form-control ${filters.seller_id ? 'border-aiG-blue-imp' : ''} pl-1`}
                        value={filters.seller_id ? filters.seller_id : ""}
                        name="seller_filter"
                        id="seller_filter"
                        onChange={(e) => getOrdersByMatchingFilters('seller_id', e.target.value)}
                      >
                        <option value={""}>{I18n.t('js.indexOrdersPage.selectSeller')}</option>
                        {sellers.map((seller) => (
                          <option value={seller.id} key={seller.id}>
                            {seller.username}
                          </option>
                        ))}
                      </select>
                    </div>
                  }


                  {
                    ordersType !== 'sent' &&
                    <div className="col-lg-2 col-md-3 col-6 mb-2">
                      <p className="mb-0">{I18n.t('js.indexOrdersPage.buyer')}</p>
                      <select
                        className={`form-control ${filters.buyer_id ? 'border-aiG-blue-imp' : ''} pl-1`}
                        value={filters.buyer_id ? filters.buyer_id : ""}
                        name="buyer_filter"
                        id="buyer_filter"
                        onChange={(e) => getOrdersByMatchingFilters('buyer_id', e.target.value)}
                      >
                        <option value={""}>{I18n.t('js.indexOrdersPage.selectBuyer')}</option>
                        {buyers.map((buyer) => (
                          <option value={buyer.id} key={buyer.id}>
                            {buyer.username}
                          </option>
                        ))}
                      </select>
                    </div>
                  }

                  {
                    ordersType !== 'sent' &&
                    <div className="col-lg-2 col-md-3 col-6 mb-2">
                      <p className="mb-0">{I18n.t('js.indexOrdersPage.shipment')}</p>
                      <select
                        className={`form-control ${filters.shipment_address_id ? 'border-aiG-blue-imp' : ''}`}
                        value={filters.shipment_address_id ? filters.shipment_address_id : ""}
                        name="shipment_filter"
                        id="shipment_filter"
                        onChange={(e) => getOrdersByMatchingFilters('shipment_address_id', e.target.value)}
                      >
                        <option value={""}>{I18n.t('js.indexOrdersPage.selectAddress')}</option>
                        {shipmentAddresses.map((shipment) => (
                          <option value={shipment.id} key={shipment.id}>
                            {shipment.address}
                          </option>
                        ))}
                      </select>
                    </div>
                  }

                  {
                    ['received', 'sent'].includes(ordersType) &&
                      <div className="col-lg-2 col-md-3 col-6 mb-2 mt-17px">
                        <button
                          type="button"
                          className="btn-standard-outline br-20px mt-1 pt-1 pb-1 btn-standard-outline-resized w-100"
                          onClick={(e) => {
                            setFromDate(null);
                            setToDate(null);
                            updateFilters(initialFilters);
                            if (window.innerWidth <= IPAD_WIDTH)
                              setFilterPannelIsOpen(false)
                          }}>
                          {I18n.t('js.filters.resetFilter')}
                        </button>
                      </div>
                  }

                  <div className="col-lg-2 col-md-3 col-6 mb-2 mt-17px show-on-mobile">
                    <button
                      className="btn-standard bg-aig-gradient text-light br-20px mt-1 pt-1 pb-1 pr-5 pl-5 btn-standard-outline-resized w-100"
                      onClick={(e) => setFilterPannelIsOpen(false)}>
                      {I18n.t('js.advancedSearch.filterComponent.search')}
                    </button>
                  </div>
                </div>

                <div className="row mb-2 mt-3">
                  <div className="col-12">
                    <h4 className="mt-0">{I18n.t('js.indexOrdersPage.sortBy')}</h4>
                  </div>
                </div>

                <div className="row">
                  <div className=" col-xs-12 col-lg-4 d-flex justify-content-start align-items-center">
                    <select
                      className={`form-control ${filters.sort_by ? 'border-aiG-blue-imp' : ''} mr-1`}
                      name="sort_by" id="sort_by" value={filters.sort_by}
                      style={{minWidth: "60%"}}
                      onChange={(e) => getOrdersByMatchingFilters('sort_by', e.target.value)}>
                      {sorting_options.map(option => {
                        return <option value={option.value} key={option.value}>{I18n.t(option.locale)}</option>
                      })}
                    </select>
                    <span>
                      <b>:</b>
                    </span>
                    <select
                      className={`form-control ${filters.sorting_direction ? 'border-aiG-blue-imp' : ''} ml-1`}
                      name="sorting_direction" id="sorting_direction"
                      value={filters.sorting_direction}
                      onChange={(e) => getOrdersByMatchingFilters('sorting_direction', e.target.value)}>
                      {sorting_directions.map(direction => {
                        return <option value={direction.value} key={direction.value}>{I18n.t(direction.locale)}</option>
                      })}
                    </select>
                  </div>

                  {
                    !['received', 'sent'].includes(ordersType) &&
                      <div className="col-lg-2 col-md-3 col-6 mb-2">
                        <button
                          type="button"
                          className="btn-standard-outline mt-1 pt-1 pb-1 btn-standard-outline-resized w-100"
                          onClick={(e) => {
                            setFromDate(null);
                            setToDate(null);
                            updateFilters(initialFilters);
                          }}>
                          {I18n.t('js.filters.resetFilter')}
                        </button>
                      </div>
                  }
                </div>
              </div>
              :
              <React.Fragment>
                <div
                  className={`${JSON.stringify(filters) !== JSON.stringify(initialFilters) && 'icon--bg-active'} rounded-right show-on-desktop expansion__filter--mobile-position top-170px`}>
                  <button id={"button_filters"} type="button" className="btn p-7px"
                          onClick={(e) => setFilterPannelIsOpen(true)}>
                    <i
                      className={`${JSON.stringify(filters) !== JSON.stringify(initialFilters) ? 'icon--color-active' : 'icon-color-standard'} fas fa-filter enable-filters-expansions-icon`}/>
                  </button>
                </div>

                <div
                  className={`${JSON.stringify(filters) !== JSON.stringify(initialFilters) && 'icon--bg-active'} rounded-right show-on-mobile expansion__filter--mobile-position`}>
                  <button id={"button_filters"} type="button" className="btn p-7px"
                          onClick={(e) => setFilterPannelIsOpen(true)}>
                    <i
                      className={`${JSON.stringify(filters) !== JSON.stringify(initialFilters) ? 'icon--color-active' : 'icon-color-standard'} fas fa-filter enable-filters-expansions-icon`}/>
                  </button>
                </div>
              </React.Fragment>
          }
        </div>

        <React.Fragment>
          {
            loading ?
              <div className="d-flex justify-content-center mt-5">
                <div className="d-flex align-items-center text-muted">
                  <div className="spinner-border" role="status"/>
                  <b className="ml-2">{I18n.t('js.indexOrdersPage.loading')}</b>
                </div>
              </div>
              :
              orders.length > 0 ?
                <BottomScrollListener onBottom={getOrders}>
                  <div className="row mt-3">
                    <div className="col-12 col-md-6">
                      <p>
                        {ordersCount}&nbsp;
                        {pluralize(ordersCount, 'indexOrdersPage.order')}
                      </p>
                    </div>
                  </div>

                  <div className="d-none d-lg-block table-responsive">
                    <table id="edit-my-collection" className="table table-hover mt-30px bg-white">
                      <thead className="thead-dark">
                      <tr>
                        <th scope="col"
                            className="text-center">{I18n.t('js.indexOrdersPage.tableHeaderIdOrder')}</th>
                        {
                          ordersType !== 'sent' &&
                          <th className="text-center">{I18n.t('js.indexOrdersPage.tableHeaderBuyer')}</th>
                        }
                        {
                          ordersType !== 'received' &&
                          <th className="text-center">{I18n.t('js.indexOrdersPage.tableHeaderSeller')}</th>
                        }
                        <th className="text-center">{I18n.t('js.indexOrdersPage.tableHeaderCreateAt')}</th>
                        <th className="text-center">{I18n.t('js.indexOrdersPage.tableHeaderState')}</th>
                        <th className="text-center">{I18n.t('js.indexOrdersPage.tableHeaderProductCount')}</th>
                        <th className=" text-center">{I18n.t('js.indexOrdersPage.tableHeaderAmount')}</th>
                        <th colSpan="2" className="text-center">{I18n.t('js.indexOrdersPage.tableHeaderShipment')}</th>
                        <th className="text-center">{I18n.t('js.indexOrdersPage.tableHeaderCourier')}</th>
                        <th className="text-center">{I18n.t('js.indexOrdersPage.tableHeaderTracking')}</th>
                        <th className="text-center">{I18n.t('js.indexOrdersPage.tableHeaderManageActions')}</th>
                      </tr>
                      </thead>
                      <tbody>
                      {orders.map((order, i) =>
                        <tr key={order.id} id={order.id}
                            className="cursor-pointer"
                            onClick={(e) =>
                              window.location = `/orders/${order.id}/edit`
                            }>
                          <td className="text-center ml-td">
                            {order.id}
                          </td>
                          {
                            ordersType !== 'sent' &&
                            <td id={"buyer_" + order.id} className="text-center ml-td">
                              {order.buyer.username}<br/>
                              {order.buyer.email}
                            </td>
                          }
                          {
                            ordersType !== 'received' &&
                            <td id={"seller_" + order.id} className="text-center ml-td">
                              {order.seller.username}<br/>
                              {order.seller.email}
                            </td>
                          }
                          <td className="text-center ml-td">
                            {order.created_at}
                          </td>
                          <td id={"state_" + order.id} className="text-center ml-td"
                              style={{
                                color: order.state === 'rejected' ? 'red' :
                                  (order.state === 'in_progress' ? 'blue' :
                                    (order.state === 'sent' ? 'darkOrange' :
                                      (order.state === 'warning' ? 'red' :
                                        (order.state === 'received' ? 'green' : 'black'))))
                              }}>

                            {
                              (order.state === 'warning' || (order.state === 'rejected' && order.rejection_reason && order.rejection_reason !== "")) &&
                              <p className="w-100 text-center mb-0">
                                <OverlayTrigger
                                  key="bottom"
                                  placement="bottom"
                                  overlay={
                                    <Tooltip id={`tooltip-bottom`}>
                                      {
                                        order.state === 'warning' ?
                                          I18n.t('js.indexOrdersPage.tooltipOrderInWarningStatus')
                                      :
                                          translateRejectionReason(order.rejection_reason)
                                      }
                                    </Tooltip>
                                  }
                                >
                                  <i className="fas fa-exclamation-triangle"
                                     onClick={(e) => e.stopPropagation()}/>
                                </OverlayTrigger>
                              </p>

                            }
                            <b>
                              {parseOrderState(order.state)}
                            </b>
                          </td>
                          <td id={"items_number_" + order.id} className="text-center ml-td">
                            {parseBigInteger(order.number_of_items)}
                          </td>
                          <td id={"price_purchase_" + order.id} className="text-center ml-td">
                            { parseAmount(order.products_price_purchase) + CURRENCY_EURO}
                          </td>
                          <td id={"address_" + order.id} colSpan="2" className="text-center ml-td">
                            {
                              order.shipment_address &&
                                <React.Fragment>
                                  {parseUserFullName(order.shipment_address.first_name, order.shipment_address.last_name)}<br/>
                                  {order.shipment_address.street_square + ' ' + order.shipment_address.number + (order.shipment_address.indoor ? ', ' + order.shipment_address.indoor : '')}<br/>
                                  {order.shipment_address.cap + ' ' + order.shipment_address.city + ' - ' + order.shipment_address.province.toUpperCase() + ' (' + order.shipment_address.country.toUpperCase() + ')'}
                                  {order.shipment_address.phone_number ?
                                    <span><br/>{order.shipment_address.phone_number}</span> : ""}
                                </React.Fragment>
                            }
                          </td>
                          <td id={"courier_" + order.id} className="text-center ml-td">
                            {order.courier && order.courier.meta_name ? order.courier.meta_name : I18n.t('js.indexOrdersPage.notDefined')}
                          </td>
                          <td id={"tracking_number_" + order.id} className="text-center ml-td">
                            {order.tracking_number ? order.tracking_number : I18n.t('js.indexOrdersPage.notDefined')}
                          </td>
                          <td className="text-center">
                            {
                              !["received", "warning", "rejected"].includes(order.state) &&
                              ordersType === 'received' && (order.state === 'opened' || order.state === 'in_progress') ?
                                <div>
                                  <button
                                    type="button"
                                    className="btn-standard bg-dark text-light pr-2 pl-2 w-120px btn-standard-resized pt-6px-imp h-auto btn-inside-table"
                                    onClick={(e) =>
                                      order.state === 'opened' ?
                                        askToUserTheChengeState(e, order, order.state, 'in_progress', null, null)
                                        :
                                        handleShowShipmentDataModal(e, order)
                                    }
                                  >
                                    {order.state === 'opened' ? I18n.t('js.indexOrdersPage.markInprograss') : I18n.t('js.indexOrdersPage.markSent')}
                                  </button>

                                  {
                                    order.state === 'opened' &&
                                    <button
                                      type="button"
                                      className="btn-standard-outline-danger pr-2 pl-2 w-120px btn-standard-outline-resized h-auto btn-inside-table mt-3"
                                      onClick={(e) =>
                                        handleAlertProblem(e, order)
                                      }
                                    >
                                      <i className="fas fa-exclamation-triangle mr-10px" />
                                      {I18n.t('js.indexOrdersPage.rejectOrder')}
                                    </button>
                                  }
                                </div>
                              :
                                ordersType === 'received' && ['sent', 'warning'].includes(order.state) && order.courier ?
                                  <button
                                    type="button"
                                    className="btn-standard bg-dark text-light pr-2 pl-2 w-120px btn-standard-resized pt-6px-imp h-auto btn-inside-table"
                                    onClick={(e) => handleShowShipmentDataModal(e, order) }
                                  >
                                    {I18n.t('js.indexOrdersPage.updateTracking')}
                                  </button>
                                :
                                  ordersType === 'sent' && ['sent', 'warning'].includes(order.state) &&
                                    <div className="d-block">
                                      <button
                                        type="button"
                                        className="btn-standard bg-dark text-light pr-2 pl-2 w-120px btn-standard-resized pt-6px-imp h-auto btn-inside-table"
                                        onClick={(e) =>
                                          askToUserTheChengeState(e, order, order.state, 'received', order.courier, order.tracking_number)
                                        }
                                      >
                                        {
                                          order.state === 'sent' ?
                                            <span>{I18n.t('js.indexOrdersPage.markReceived')}</span>
                                            :
                                            <span>{I18n.t('js.indexOrdersPage.markProblemSolved')}</span>
                                        }
                                      </button>

                                      {
                                        order.state !== 'warning' && order.can_warn_a_problem &&
                                        <button
                                          type="button"
                                          className="btn-standard-outline-danger pr-2 pl-2 w-120px btn-standard-outline-resized h-auto btn-inside-table mt-3"
                                          onClick={(e) => handleAlertProblem(e, order)}>
                                          <i className="fas fa-exclamation-triangle mr-10px" />
                                          {I18n.t('js.indexOrdersPage.markProblem')}
                                        </button>
                                      }
                                    </div>
                            }
                          </td>
                        </tr>
                      )}
                      </tbody>
                    </table>
                  </div>

                  <div className="show-on-mobile-d-block">
                    {orders.map((order, i) =>
                      <Accordion defaultActiveKey={orders[0].id} className="w-100 mb-3 border-aiG-black-imp br-8px" key={order.id}>
                        <Card className="bg-white border-none border-round-standard">
                          <Accordion.Toggle as={Card.Header} eventKey={order.id}>
                            <div className="row">
                              <div className="col-6 col-md-3 p-0 text-center">
                                <p className="mb-0 text-weight-500"> {I18n.t('js.indexOrdersPage.tableHeaderIdOrder')}: &nbsp; {order.id}</p>
                              </div>
                              <div className="col-6 col-md-3 p-0 text-center">
                                <p className="mb-0 text-weight-500" style={{
                                  color: order.state === 'rejected' ? 'red' :
                                    (order.state === 'in_progress' ? 'blue' :
                                      (order.state === 'sent' ? 'darkOrange' :
                                        (order.state === 'warning' ? 'red' :
                                          (order.state === 'received' ? 'green' : 'black'))))
                                }}> {parseOrderState(order.state)}</p>
                              </div>
                              <div className="col-6 col-md-3 p-0 text-center">
                                <p className="mb-0 text-weight-500"> {I18n.t('js.indexOrdersPage.tableHeaderProductCount')}:&nbsp;{parseBigInteger(order.number_of_items)}</p>
                              </div>
                              <div className="col-6 col-md-3 p-0 text-center">
                                <p className="mb-0 text-weight-500">{ parseAmount(order.products_price_purchase) + CURRENCY_EURO }</p>
                              </div>
                            </div>
                          </Accordion.Toggle>
                          <Accordion.Collapse eventKey={order.id}
                                              onClick={(e) =>
                                                window.location = `/orders/${order.id}/edit`
                                              }
                          >
                            <div className="row mt-10px pb-3">
                              <div className="col-12">
                                <div className="row">
                                  <div className="col-xs-6 col-md-3">
                                    <p className="pl-20px text-weight-500">{I18n.t('js.filters.state')}</p>
                                  </div>
                                  <div style={{
                                    color: order.state === 'rejected' ? 'red' :
                                      (order.state === 'in_progress' ? 'blue' :
                                        (order.state === 'sent' ? 'darkOrange' :
                                          (order.state === 'warning' ? 'red' :
                                            (order.state === 'received' ? 'green' : 'black'))))
                                  }}
                                       className="col-xs-6 col-md-3"
                                       onClick={(e) => e.stopPropagation()}
                                  >
                                    <p className="pr-20px text-right text-md-left">
                                      <b>{parseOrderState(order.state)}</b>
                                      {
                                        (order.state === 'warning' || (order.state === 'rejected' && order.rejection_reason && order.rejection_reason !== "")) &&
                                        <OverlayTrigger
                                          key="bottom"
                                          placement="bottom"
                                          overlay={
                                            <Tooltip id={`tooltip-bottom`}>
                                              {
                                                order.state === 'warning' ?
                                                  I18n.t('js.indexOrdersPage.tooltipOrderInWarningStatus')
                                                  :
                                                  translateRejectionReason(order.rejection_reason)
                                              }
                                            </Tooltip>
                                          }
                                        >
                                          <i className="fas fa-exclamation-triangle"
                                             onClick={(e) => e.stopPropagation()}/>
                                        </OverlayTrigger>
                                      }
                                    </p>
                                  </div>

                                  <div className="col-xs-6 col-md-3">
                                    <p
                                      className="pl-20px text--white-space-no-wrap text-weight-500">{I18n.t('js.indexOrdersPage.tableHeaderCreateAt')}:</p>
                                  </div>
                                  <div className="col-xs-6  col-md-3">
                                    <p className="pr-20px text-right text-md-left">
                                      {order.created_at}
                                    </p>
                                  </div>
                                </div>

                                <div className="row">

                                  <div className="col-xs-6 col-md-3">
                                    <p
                                      className="pl-20px text-weight-500 text--white-space-no-wrap">{I18n.t('js.indexOrdersPage.tableHeaderProductCount')}:</p>
                                  </div>

                                  <div className="col-xs-6 col-md-3">
                                    <p
                                      className="pr-20px text-right text-md-left">{parseBigInteger(order.number_of_items)}</p>
                                  </div>

                                  <div className="col-xs-6 col-md-3">
                                    <p
                                      className="pl-20px text-weight-500">{I18n.t('js.indexOrdersPage.tableHeaderAmount')}:</p>
                                  </div>

                                  <div className="col-xs-6 col-md-3">
                                    <p
                                      className="pr-20px text-right text-md-left">{parseAmount(order.products_price_purchase) + CURRENCY_EURO}</p>
                                  </div>

                                </div>


                                <div className="row">
                                  {
                                    ordersType !== 'sent' &&
                                    <React.Fragment>
                                      <div className="col-xs-6 col-md-3">
                                        <p
                                          className="pl-20px text--white-space-no-wrap text-weight-500">{I18n.t('js.indexOrdersPage.tableHeaderBuyer')}:</p>
                                      </div>

                                      <div className="col-xs-12 col-md-9 d-md-flex">
                                        <p className="pr-20px mb-0 text-right text-md-left">{order.buyer.username}</p>
                                        <p className="pr-20px text-right text-md-left">{order.buyer.email}</p>
                                      </div>
                                    </React.Fragment>
                                  }
                                  {
                                    ordersType !== 'received' &&
                                    <React.Fragment>
                                      <div className="col-xs-3 col-md-3">
                                        <p
                                          className="pl-20px text--white-space-no-wrap text-weight-500">{I18n.t('js.indexOrdersPage.tableHeaderSeller')}:</p>
                                      </div>
                                      <div className="col-xs-12 col-md-9 d-md-flex">
                                        <p className="pr-20px mb-0 text-right text-md-left">{order.seller.username}</p>
                                        <p className="pr-20px text-right text-md-left">{order.seller.email}</p>
                                      </div>
                                    </React.Fragment>
                                  }
                                </div>

                                <div className="row">
                                  <div className="col-xs-6 col-md-3">
                                    <p
                                      className="pl-20px text--white-space-no-wrap text-weight-500 tablet-text-white-space-uset">{I18n.t('js.indexOrdersPage.tableHeaderShipment')}:</p>
                                  </div>
                                  <div className="col-xs-12 col-md-9 ">
                                    {
                                      order.shipment_address &&
                                      <React.Fragment>
                                        <p
                                          className="pr-20px mb-0 text-right text-md-left">{order.shipment_address.first_name + ' ' + order.shipment_address.last_name + ' ' +
                                        order.shipment_address.street_square + ' ' + order.shipment_address.number + (order.shipment_address.indoor ? ', ' + order.shipment_address.indoor : '')}</p>

                                        <p
                                          className="pr-20px mb-0 text-right text-md-left">{order.shipment_address.city + ' ' + order.shipment_address.cap}</p>

                                        <p
                                          className="pr-20px text-right mb-0 text-md-left">{order.shipment_address.province.toUpperCase() + ' ' + '(' + order.shipment_address.country.toUpperCase() + ')'}</p>
                                        <p
                                          className="pr-20px text-right text-md-left">{order.shipment_address.phone_number &&
                                        I18n.t('js.indexOrdersPage.phone') + ' ' + order.shipment_address.phone_number}</p>
                                      </React.Fragment>
                                    }
                                  </div>
                                </div>

                                <div className="row">
                                  <div className="col-xs-6 col-md-3">
                                    <p className="pl-20px text--white-space-no-wrap text-weight-500">
                                      {
                                        I18n.t('js.indexOrdersPage.tableHeaderCourier')
                                      }:
                                    </p>
                                  </div>

                                  <div className="col-xs-6 col-md-3">
                                    <p className="pr-20px text-right text-md-left pr-md-0">
                                      {
                                        order.courier && order.courier.meta_name ? order.courier.meta_name : I18n.t('js.indexOrdersPage.notDefined')
                                      }
                                    </p>
                                  </div>


                                  <div className="col-xs-6 col-md-3">
                                    <p
                                      className="pl-20px text--white-space-no-wrap text-weight-500 small-smartphone-text-white-space-uset">{I18n.t('js.indexOrdersPage.tableHeaderTracking')}:</p>
                                  </div>

                                  <div className="col-xs-6 col-md-3">
                                    <p
                                      className="pr-20px text-right text-md-left pr-md-0">{order.tracking_number ? order.tracking_number : I18n.t('js.indexOrdersPage.notDefined')}</p>
                                  </div>
                                </div>

                                <div className="row d-md-flex justify-content-end">
                                  {
                                    !["received", "warning", "rejected"].includes(order.state) &&
                                    ordersType === 'received' && (order.state === 'opened' || order.state === 'in_progress') ?
                                      <React.Fragment>
                                        <div className="col-xs-6 col-md-4">
                                          {
                                            order.state === 'opened' &&
                                            <button
                                              type="button"
                                              className="btn-standard-outline-danger pr-2 pl-2 w-120px btn-standard-outline-resized h-auto d-flex ml-20px-imp tablet-w-57-floatR d-flex align-items-center"
                                              onClick={(e) =>
                                                handleAlertProblem(e, order)
                                              }
                                            >
                                              {I18n.t('js.indexOrdersPage.rejectOrder')}
                                              <i className="fas fa-exclamation-triangle" />
                                            </button>
                                          }
                                        </div>
                                        <div className="col-xs-6 col-md-3">
                                          <button
                                            type="button"
                                            className="btn-standard bg-dark text-light pr-2 pl-2 w-120px btn-standard-resized pt-6px-imp h-auto mr-20px float-right"
                                            onClick={(e) =>
                                              order.state === 'opened' ?
                                                askToUserTheChengeState(e, order, order.state, 'in_progress', null, null)
                                                :
                                                handleShowShipmentDataModal(e, order)
                                            }
                                          >
                                            {order.state === 'opened' ? I18n.t('js.indexOrdersPage.markInprograss') : I18n.t('js.indexOrdersPage.markSent')}
                                          </button>
                                        </div>
                                      </React.Fragment>
                                    :
                                      ordersType === 'received' && ['sent', 'warning'].includes(order.state) && order.courier ?
                                        <button
                                          type="button"
                                          className="btn-standard bg-dark text-light pr-2 pl-2 max-w-200px btn-standard-resized pt-6px-imp h-auto mr-20px float-right"
                                          onClick={(e) => handleShowShipmentDataModal(e, order) }
                                        >
                                          {I18n.t('js.indexOrdersPage.updateTracking')}
                                        </button>
                                      :
                                        ordersType === 'sent' && ['sent', 'warning'].includes(order.state) &&
                                          <React.Fragment>
                                            <div className="col-xs-6 col-md-4">
                                              {
                                                order.state !== 'warning' && order.can_warn_a_problem &&
                                                <button
                                                  type="button"
                                                  className="btn-standard-outline-danger pr-2 pl-2 w-120px btn-standard-outline-resized h-auto d-flex ml-20px-imp tablet-w-57-floatR d-flex align-items-center"
                                                  onClick={(e) => handleAlertProblem(e, order)}>

                                                  {I18n.t('js.indexOrdersPage.markProblem')}
                                                  <i className="fas fa-exclamation-triangle" />
                                                </button>
                                              }
                                            </div>
                                            <div className="col-xs-6 col-md-3">
                                              <button
                                                type="button"
                                                className="btn-standard bg-dark text-light pr-2 pl-2 w-120px btn-standard-resized pt-6px-imp h-auto mr-20px float-right"
                                                onClick={(e) =>
                                                  askToUserTheChengeState(e, order, order.state, 'received', order.courier, order.tracking_number)
                                                }
                                              >
                                                {
                                                  order.state === 'sent' ?
                                                    <span>{I18n.t('js.indexOrdersPage.markReceived')}</span>
                                                    :
                                                    <span>{I18n.t('js.indexOrdersPage.markProblemSolved')}</span>
                                                }
                                              </button>
                                            </div>
                                          </React.Fragment>
                                  }

                                </div>
                              </div>
                            </div>
                          </Accordion.Collapse>
                        </Card>
                      </Accordion>
                    )}
                  </div>

                  {
                    !hasLoadedAllOrders && orders.length > 0 &&
                    <div className="d-flex justify-content-center mt-5">
                      <div className="d-flex align-items-center text-muted">
                        <div className="spinner-border" role="status"/>
                        <b className="ml-2">{I18n.t('js.common.loading')}</b>
                      </div>
                    </div>

                  }
                </BottomScrollListener>
                :
                <div className="d-flex justify-content-center mt-2 pt-3">
                  <div className="d-flex align-items-center text-muted">
                    <p
                      className="text-center text--size-18px text-space text-weight-500">{I18n.t('js.indexOrdersPage.noOrdersFound')}</p>
                  </div>
                </div>
          }
        </React.Fragment>
      </div>

      <SuccessFlashMessage
        showSuccessFlashMessage={showSuccessFlashMessage}
        setShowSuccessFlashMessage={setShowSuccessFlashMessage}
      />

      <ErrorModal
        showErrorModal={showErrorModal}
        setShowErrorModal={setShowErrorModal}
      />

      <ShipmentDataModal
        dataStructureToSetShipmentData={orderToSetShipmentData}
        setDataStructureToSetShipmentData={setOrderToSetShipmentData}
        couriers={couriers}
        onChangeOrderStateHandler={onChangeOrderStateHandler}
        dataToUpdate={shipmentDataToUpdate}
        setDataToUpdate={setShipmentDataToUpdate}
        enableCustomCourier={true}
      />

      <RejectingModal
        itemToReject={orderToReject}
        setItemToReject={setOrderToReject}
        rejectingCauses={orderToReject && orderToReject.state === 'opened' ? possibleOrdersRejectingCauses : possibleRejectingCauses}
        rejectingCause={rejectingCause}
        handleRejectingCause={handleRejectingCause}
        otherRejectingCause={otherRejectingCause}
        handleSetOtherRejectingCause={handleSetOtherRejectingCause}
        handleReject={handleReject}
        global={true}
        rejectConfirmMessage={I18n.t('js.modalRejecting.confirmRejectOrderContent')}
      />

      <FullScreenLoadingOverlay
        showFullScreenLoadingOverlay={isOrderInProgress}
        textFullScreenLoadingOverlay={I18n.t('js.editOrderPage.orderInProgress')}
      />

      {
        activeOrder &&
        <AlertPopup
          showAlertPopup={showChangeStateAlertPopup}
          onSuccess={() => onChangeOrderStateHandler(null, activeOrder, 'state', activeOrderKey, activeOrderState, activeOrderCourier ? activeOrderCourier.id : null, activeOrderCourier ? activeOrderCourier.meta_name : null, activeOrderTrackingNumber)}
          onDiscard={setShowChangeStateAlertPopup}
          popupTitle={`${I18n.t('js.indexOrdersPage.modalChangeOrderStatusTilte')}`}
          popupContent={setaskToUserText(activeOrderKey, activeOrderState)}
          cancelButtonText={`${I18n.t('js.common.button.cancel').toUpperCase()}`}
          confirmButtonText={`${I18n.t('js.common.button.confirm').toUpperCase()}`}
        />
      }

    </React.Fragment>
  );
};

export default IndexOrders;